import { CULL_MARGIN, DT, OBSTACLE_SCROLL_SPEED, PF_HH } from '../constants';
import { cos, sin, TAU } from '../math';
import type { World } from '../World';

/* ═══════════════════════════════════════════════════════════════════════════
   Le défilement des OBSTACLES.

   Un obstacle est du décor collisionnable. Il ne pilote pas de trajectoire comme
   un ennemi : il DESCEND au rythme du monde, à `OBSTACLE_SCROLL_SPEED`, la même
   vitesse que la poussière et la tranchée du décor. C'est ce qui le fait lire
   comme « une partie du terrain qui vient vers moi » et non comme un adversaire.

   Ce système ne fait donc que deux choses : avancer les obstacles vers le bas et
   CULER ceux qui sont sortis par le bas. La collision (tirs bloqués, contact avec
   le joueur, destruction) vit dans `collisionSystem`, comme pour tout le reste.

   ⚠️ AUCUNE allocation par tick, et itération EN ARRIÈRE : la boucle libère, et
   `SoAPool.free` déplace le dernier vivant sur la place libérée — une boucle en
   avant sauterait l'élément déplacé (piège documenté dans SoAPool).
   ═══════════════════════════════════════════════════════════════════════════ */

/** Ordonnée sous laquelle un obstacle est définitivement hors jeu. La même marge
 *  que pour les ennemis et les projectiles : assez basse pour qu'un obstacle
 *  descendu au niveau du joueur ait fini de le menacer avant d'être libéré. */
const OUT_Y = PF_HH + CULL_MARGIN;

/** Amortissement de la vitesse PROPRE des fragments, par seconde. Un fragment
 *  s'ouvre vite depuis le point d'impact puis rejoint le simple défilement en ~½ s
 *  — assez pour lire une explosion, pas assez pour qu'un débris parte en voyage. */
const FRAG_DAMP = 3.2;

export function obstacleSystem(w: World): void {
  const ob = w.obstacles;
  // Déplacement de défilement par tick, calculé UNE fois : le pas est le même pour
  // tous, un obstacle ne choisit pas sa vitesse de descente.
  const dy = OBSTACLE_SCROLL_SPEED * DT;
  // Facteur d'amortissement du tick, borné à zéro : un DT énorme ne doit pas
  // rendre la vitesse négative (inversion de sens).
  const damp = 1 - FRAG_DAMP * DT > 0 ? 1 - FRAG_DAMP * DT : 0;

  for (let i = ob.count - 1; i >= 0; i--) {
    const s = ob.at(i);
    // Style maison : on lit dans une locale, on calcule, on réécrit. `arr[i] += x`
    // est refusé par TS sur une cible de tableau typé, et l'accès borné ne doit
    // pas être répété.
    const vx = ob.vx[s]!;
    const vy = ob.vy[s]!;

    /* Mouvement propre de PLATEFORME VOLANTE (dérive + ondulation), en plus du
       défilement. Calculé depuis l'ÂGE — la vitesse d'ondulation est la dérivée
       de `swayAmp·sin(ω·age)`, soit `swayAmp·ω·cos(ω·age)` — donc aucun état
       partagé et un déterminisme parfait. Inerte sur un obstacle sans `drift`. */
    const age = ob.age[s]! + DT;
    ob.age[s] = age;
    let ddx = 0;
    const drift = w.defs.obstacles[ob.type[s]!]?.drift;
    if (drift) {
      ddx = drift.driftX ?? 0;
      const amp = drift.swayAmp ?? 0;
      const period = drift.swayPeriod ?? 0;
      if (amp !== 0 && period > 0) {
        const wsw = TAU / period;
        ddx += amp * wsw * cos(wsw * age);
      }
    }

    // Défilement commun (−dy) PLUS la vitesse propre du fragment PLUS la dérive de
    // plateforme. Le segment balayé (px,py)→(x,y) que lit `collisionSystem` couvre
    // les trois composantes.
    ob.x[s] = ob.x[s]! + (vx + ddx) * DT;
    const y = ob.y[s]! - dy + vy * DT;
    ob.y[s] = y;
    // Amortir la vitesse propre du FRAGMENT (jamais la dérive de plateforme, qui
    // n'est pas stockée dans vx/vy). Sur un obstacle ordinaire vx/vy valent zéro et
    // le restent ; on n'écrit que quand il y a quelque chose à amortir.
    if (vx !== 0 || vy !== 0) {
      ob.vx[s] = vx * damp;
      ob.vy[s] = vy * damp;
    }

    // Sorti par le bas : plus rien à bloquer ni à esquiver. Contrairement aux
    // ennemis, aucune pénalité de fuite — un obstacle « laissé passer » a été
    // esquivé, ce qui est un succès, pas un échec.
    if (y < -OUT_Y) ob.free(s);
  }

  positionRiders(w);
}

/* ── CHEVAUCHEURS ─────────────────────────────────────────────────────────────
   Les ennemis MONTÉS sur une plateforme (obstacle porteur). Repositionnés ICI,
   APRÈS le déplacement de tous les obstacles — jamais dans `motionSystem`, qui
   tourne AVANT `obstacleSystem` et leur ferait accuser un tick de retard, le canon
   qui traîne derrière son radeau (la régression exacte déjà vécue sur les parties
   de boss). Un chevaucheur n'a pas de trajectoire propre : il est boulonné à la
   plateforme, à son décalage. Quand le porteur disparaît — sorti par le bas ou
   détruit — le chevaucheur part AVEC lui : il est libéré. Boucle EN ARRIÈRE. */
function positionRiders(w: World): void {
  const en = w.enemies;
  const ob = w.obstacles;
  for (let i = en.count - 1; i >= 0; i--) {
    const s = en.at(i);
    const carrier = en.obCarrier[s]!;
    if (carrier < 0) continue;
    // Porteur mort ou slot recyclé par un autre obstacle : le lien est rompu, le
    // chevaucheur quitte l'écran avec sa plateforme.
    if (!ob.slots.isAlive(carrier) || ob.uid[carrier]! !== en.obCarrierUid[s]!) {
      en.free(s);
      continue;
    }
    en.x[s] = ob.x[carrier]! + en.offX[s]!;
    en.y[s] = ob.y[carrier]! + en.offY[s]!;
  }
}

/** Fait apparaître un obstacle de contenu au sommet du terrain, à l'abscisse
 *  `x`. Le pendant de `spawnFromDef` côté ennemis, en plus simple : pas de
 *  couloirs, pas de coefficients de monde, pas de bouclier ni de parties.
 *
 *  ⚠️ Il ne tire JAMAIS le RNG. Un obstacle naît à une position DÉTERMINISTE
 *  fournie par l'appelant (une vitrine, une future timeline) : consommer l'aléa
 *  ici décalerait le flux de gameplay et invaliderait les golden replays, pour
 *  aucun gain — la dispersion verticale des ennemis existe parce qu'ils arrivent
 *  en nuées, un rocher se place à la main.
 *
 *  Retourne le slot, ou -1 si l'identifiant est inconnu ou le pool saturé (déjà
 *  comptabilisé en overflow dans ce dernier cas). */
export function spawnObstacleFromDef(w: World, obstacleId: string, x: number, y = OUT_Y): number {
  const idx = w.defs.obstacleByI.get(obstacleId);
  if (idx === undefined) return -1;
  const def = w.defs.obstacles[idx];
  if (!def) return -1;

  // Absent vaut « destructible » : le cas courant est un petit astéroïde qu'on
  // fait sauter. L'indestructibilité est un choix explicite du contenu.
  const destructible = def.destructible === false ? 0 : 1;
  const slot = w.obstacles.spawn(idx, x, y, def.radius, def.hp, destructible);
  if (slot < 0) {
    w.overflow.obstacles++;
    return -1;
  }
  return slot;
}

/** Au-dessus de ce rayon, un obstacle détruit se BRISE en fragments plutôt que de
 *  disparaître d'un coup. En dessous (un petit éclat), il n'y a rien à casser : la
 *  gerbe cosmétique de la vue suffit. */
export const SHATTER_MIN_RADIUS = 34;

/** Silhouette des fragments : on réemploie l'éclat, à une taille RECALCULÉE par
 *  morceau. La variété de taille vient du calcul, pas de la définition. */
const FRAGMENT_OBSTACLE = 'astero-eclat';

/** Fractions de taille des fragments. Distinctes à dessein — un rocher ne se brise
 *  pas en parts égales. La taille finale est en plus BORNÉE à `SHATTER_MIN_RADIUS`
 *  (voir `shatterObstacle`), ce qui interdit toute cascade quelle que soit la
 *  taille du parent. */
const FRAGMENT_FRACS: readonly number[] = [0.5, 0.36, 0.46, 0.3, 0.42, 0.33];

/**
 * Brise un obstacle détruit en fragments plus petits, de TAILLES VARIÉES, qui
 * s'écartent du point d'impact avec leur vitesse propre.
 *
 * ⚠️ DÉTERMINISTE : aucun tirage RNG. La variété vient d'un motif indexé et d'une
 * phase dérivée de la position, pas de l'aléa — deux rochers brisés au même
 * endroit donnent les mêmes éclats, ce que le golden replay exige. Consommer le
 * RNG de gameplay ici décalerait tout le flux (émission ennemie, dispersion) pour
 * un simple effet de décor.
 *
 * No-op si l'obstacle est trop petit, ou si le type de fragment est absent du
 * catalogue (index de test minimal) — la dégradation est silencieuse et sûre.
 */
export function shatterObstacle(w: World, x: number, y: number, r: number): void {
  if (r < SHATTER_MIN_RADIUS) return;
  const idx = w.defs.obstacleByI.get(FRAGMENT_OBSTACLE);
  if (idx === undefined) return;

  // Plus le rocher est gros, plus il éclate en morceaux : un bloc en quatre, un
  // noyau en cinq, un mastodonte en « pleins de sous morceaux ». Plafonné à six
  // pour ne pas noyer le pool d'un coup.
  const count = r >= 58 ? 6 : r >= 46 ? 5 : 4;
  // Phase propre au rocher, dérivée de sa position tronquée : deux rochers voisins
  // n'éclatent pas selon la même étoile. Les tables trigonométriques absorbent une
  // phase de n'importe quelle amplitude.
  const phase = (x | 0) * 0.013 + (y | 0) * 0.017;

  for (let k = 0; k < count; k++) {
    // Taille bornée sous le seuil : un fragment ne re-casse jamais, quelle que
    // soit la taille du parent.
    let fr = r * FRAGMENT_FRACS[k % FRAGMENT_FRACS.length]!;
    if (fr > SHATTER_MIN_RADIUS - 2) fr = SHATTER_MIN_RADIUS - 2;
    const fhp = Math.round(fr * 1.1);
    const ang = phase + (k / count) * TAU;
    const ca = cos(ang);
    const sa = sin(ang);
    // On décolle le fragment du centre pour qu'ils ne naissent pas empilés, et on
    // lui donne une vitesse d'écartement — les morceaux fuient l'impact.
    const off = r * 0.3;
    const speed = 70 + (k % 3) * 34;
    const slot = w.obstacles.spawn(
      idx,
      x + ca * off,
      y + sa * off,
      fr,
      fhp > 0 ? fhp : 1,
      1,
      ca * speed,
      sa * speed,
    );
    // Pool saturé : `spawn` renvoie -1, on le compte et on cesse d'insister —
    // inutile de tenter les fragments suivants si le pool est plein.
    if (slot < 0) {
      w.overflow.obstacles++;
      break;
    }
  }
}
